<template>
    <Teleport to="body">
        <div v-if="open" class="cp-overlay" @click.self="close">
            <div
                ref="panel"
                class="cp-panel"
                role="dialog"
                aria-modal="true"
                aria-label="Search Forkto"
                @keydown="onPanelKey"
            >
                <div class="cp-input-row">
                    <button
                        v-if="isMobile"
                        type="button"
                        class="cp-icon-button"
                        aria-label="Close search"
                        data-cy="command-palette-mobile-close"
                        @click="close"
                    >
                        <i class="fas fa-arrow-left" aria-hidden="true"></i>
                    </button>
                    <i v-else class="fas fa-search cp-search-icon" aria-hidden="true"></i>
                    <input
                        ref="input"
                        v-model="query"
                        class="cp-input"
                        type="search"
                        placeholder="Search pages, actions, records and help…"
                        aria-label="Search Forkto"
                        autocomplete="off"
                        spellcheck="false"
                        enterkeyhint="go"
                        @keydown="onKey"
                    />
                    <button
                        v-if="query"
                        type="button"
                        class="cp-icon-button"
                        aria-label="Clear search"
                        @click="clearQuery"
                    >
                        <i class="fas fa-times" aria-hidden="true"></i>
                    </button>
                </div>

                <div ref="list" class="cp-list">
                    <template v-if="resultGroups.length">
                        <section v-for="group in resultGroups" :key="group.id" class="cp-group">
                            <div class="cp-group-title">{{ group.label }}</div>
                            <button
                                v-for="item in group.items"
                                :key="item.id"
                                type="button"
                                :data-command-id="item.id"
                                :data-result-index="resultIndex(item)"
                                :class="[
                                    'cp-item',
                                    resultIndex(item) === selectedIndex ? 'cp-item-active' : ''
                                ]"
                                @click="select(item)"
                                @mouseenter="preview(item)"
                            >
                                <i :class="['fas', `fa-${item.icon}`, 'cp-item-icon']"></i>
                                <div class="cp-item-text">
                                    <div class="cp-item-label">{{ item.label }}</div>
                                    <div v-if="itemMeta(item)" class="cp-item-section">
                                        {{ itemMeta(item) }}
                                    </div>
                                </div>
                                <span v-if="item.kind" class="cp-kind">{{ item.kind }}</span>
                                <i
                                    :class="[
                                        'fas',
                                        item.href ? 'fa-external-link-alt' : 'fa-arrow-right',
                                        'cp-item-arrow'
                                    ]"
                                ></i>
                            </button>
                        </section>
                    </template>
                    <div v-else class="cp-empty">
                        <i class="fas fa-search" aria-hidden="true"></i>
                        <span>No matching pages, actions, records or help</span>
                    </div>
                </div>

                <div v-if="!isMobile" class="cp-hints">
                    <span><kbd>↑</kbd><kbd>↓</kbd> Navigate</span>
                    <span><kbd>↵</kbd> Open</span>
                    <span><kbd>Esc</kbd> Close</span>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script>
import { visibleSections } from "./common/sidebarConfig.js";
import { visibleSettingsSections } from "./common/settingsConfig.js";
import { visibleCommandActions } from "./common/commandPaletteConfig.js";
import { DASHBOARD_SECTIONS, visibleReports } from "@/views/reports/reportConfig.js";
import { PLAYGROUND_TOOLS } from "@/views/playground/tools";
import { Flags, hasFeatureFlag } from "@/classes/FeatureFlags";
import { userIsRole } from "@/services/AuthService";
import { API } from "@/services/apiInstance";
import { emitter } from "@/services/emitter";
import { SCAN_LABEL_OPEN } from "@/composables/useUniversalRecordScan";
import { resolveHelpForRoute } from "@/composables/useContextHelp";
import { loadHelpSearchIndex } from "@/services/helpSearchIndex";
import { preloadRoute } from "@/services/routePreload";
import { dialogTrigger } from "@/composables/useDialogFocus";
import {
    hideMobileNavigation,
    mobileNavigation,
    mobileNavigationLauncher
} from "@/services/sidebarFocus";
import SelectorEngine from "bootstrap/js/dist/dom/selector-engine.js";
import {
    flattenCommandGroups,
    groupedCommandResults,
    GROUP_LABELS
} from "@/services/commandPaletteSearch";

import { usesPlanFeatureScope } from "@/classes/customerPlans";

const RECENT_LIMIT = 6;
const REMOTE_QUERY_LENGTH = 3;
const REMOTE_CACHE_TTL = 60000;

export default {
    data() {
        return {
            open: false,
            returnFocusTo: null,
            query: "",
            selectedIndex: 0,
            helpItems: [],
            remoteRecords: [],
            remoteSearchTimer: null,
            remoteSearchController: null,
            remoteSearchCache: new Map(),
            recentIDs: [],
            acceptAsyncResults: false,
            nativeBackHandler: null,
            nativeBackRegistration: 0
        };
    },
    computed: {
        canAccessDevMode() {
            return this.$store.getters.canAccessDevMode();
        },
        isMobile() {
            return this.$store.state.isMobile;
        },
        isMobileOS() {
            return this.$store.state.isMobileOS;
        },
        visibilityContext() {
            return {
                has: (flag) => hasFeatureFlag(flag),
                role: (name) => userIsRole(name),
                dev: this.canAccessDevMode,
                planScoped: usesPlanFeatureScope(this.$store.state.client, this.$store.state.devOverrides),
                isMobile: this.isMobile,
                isMobileOS: this.isMobileOS
            };
        },
        flatItems() {
            const items = [];
            for (const section of visibleSections(this.visibilityContext)) {
                if (section.id === "promo") continue;
                for (const item of section.items) {
                    const group =
                        item.action === "support" ||
                        item.action === "scan-label" ||
                        item.action === "help-page"
                            ? "actions"
                            : item.action
                              ? "utilities"
                              : "pages";
                    items.push({
                        id: `${section.id}.${item.id}`,
                        label: item.label,
                        icon: item.icon,
                        route: item.route,
                        path: item.path,
                        href: item.href,
                        action: item.action,
                        group,
                        kind:
                            group === "pages" ? "Page" : group === "actions" ? "Action" : "Utility",
                        sectionTitle: section.title || "",
                        keywords: item.keywords || [],
                        description: item.description || ""
                    });
                }
            }
            for (const section of visibleSettingsSections(this.visibilityContext)) {
                items.push({
                    id: `settings.${section.id}`,
                    label: section.label,
                    icon: section.icon,
                    path: `/settings/${section.id}`,
                    group: "pages",
                    kind: "Setting",
                    sectionTitle: "Settings",
                    keywords: section.keywords || [],
                    description: section.description || ""
                });
            }
            const reportSections = Object.fromEntries(
                DASHBOARD_SECTIONS.map((section) => [section.id, section.title])
            );
            for (const report of visibleReports(this.visibilityContext)) {
                items.push({
                    id: `report.${report.type.toLowerCase()}`,
                    label: report.title,
                    icon: report.icon,
                    route: report.routeName,
                    group: "pages",
                    kind: "Report",
                    sectionTitle: `Reports · ${reportSections[report.dashboardSection]}`,
                    keywords: ["report", "reports", `${report.title} report`],
                    description: report.description || ""
                });
            }
            if (this.canAccessDevMode && this.$router.hasRoute("Playground")) {
                items.push({
                    id: "playground.index",
                    label: "Playground",
                    icon: "umbrella-beach",
                    route: "Playground",
                    group: "pages",
                    kind: "Page",
                    sectionTitle: "Developer"
                });
                for (const tool of PLAYGROUND_TOOLS) {
                    items.push({
                        id: `playground.${tool.path}`,
                        label: `Playground · ${tool.label}`,
                        icon: tool.icon,
                        route: tool.name,
                        group: "pages",
                        kind: "Page",
                        sectionTitle: "Developer"
                    });
                }
            }
            return items;
        },
        actionItems() {
            return [
                ...visibleCommandActions(this.visibilityContext).map((item) => ({
                    ...item,
                    group: "actions",
                    kind: "Action",
                    sectionTitle: "Workflow"
                })),
                ...this.checkFormActions,
                ...this.contextActions
            ];
        },
        checkFormActions() {
            if (
                !hasFeatureFlag(Flags.ChecksModule) ||
                userIsRole("auditor") ||
                userIsRole("supplier")
            )
                return [];
            const enabled = this.$store.state.client?.check_forms || [];
            const locationID = this.$store.state.locationID;
            const departmentID = this.$store.state.departmentID;
            return (this.$store.state.checkFormList || [])
                .filter((form) => enabled.includes(form.id))
                .filter((form) => !locationID || form.location_ids?.includes(locationID))
                .filter(
                    (form) =>
                        !departmentID ||
                        !form.department_ids?.length ||
                        form.department_ids.includes(departmentID)
                )
                .map((form) => ({
                    id: `check-form.${form.id}`,
                    label: `Start ${form.title}`,
                    icon: "clipboard-check",
                    route: "ChecksView",
                    routeQuery: { form: form.id },
                    group: "actions",
                    kind: "Check form",
                    sectionTitle: "Checks",
                    keywords: ["start check", "complete check", form.reference].filter(Boolean),
                    description: form.description || "Open this check form"
                }));
        },
        contextActions() {
            if (userIsRole("supplier")) return [];
            const isAdmin = userIsRole("admin");
            const user = this.$store.state.user;
            const allowedLocations = isAdmin ? null : new Set(user?.location_ids || []);
            const allowedDepartments = isAdmin ? null : new Set(user?.department_ids || []);
            const locations = (this.$store.state.locationList || [])
                .filter((item) => item.id !== this.$store.state.locationID)
                .filter((item) => !allowedLocations || allowedLocations.has(item.id))
                .map((item) => ({
                    id: `switch-location.${item.id}`,
                    label: `Switch to ${item.name}`,
                    icon: "map-marker-alt",
                    action: "switch-location",
                    targetID: item.id,
                    group: "actions",
                    kind: "Location",
                    sectionTitle: "Change current location",
                    keywords: ["location", "site", "branch"]
                }));
            const departmentList =
                this.canAccessDevMode || hasFeatureFlag(Flags.DepartmentsModule)
                    ? this.$store.state.departmentList || []
                    : [];
            const departments = departmentList
                .filter((item) => item.id !== this.$store.state.departmentID)
                .filter((item) => !allowedDepartments || allowedDepartments.has(item.id))
                .map((item) => ({
                    id: `switch-department.${item.id}`,
                    label: `Switch to ${item.name}`,
                    icon: "building",
                    action: "switch-department",
                    targetID: item.id,
                    group: "actions",
                    kind: "Department",
                    sectionTitle: "Change current department",
                    keywords: ["department", "team", "area"]
                }));
            return [...locations, ...departments];
        },
        localRecordItems() {
            const items = [];
            if (hasFeatureFlag(Flags.RecipesModule) && !userIsRole("supplier")) {
                for (const recipe of this.$store.state.recipeList || []) {
                    items.push({
                        id: `recipe.${recipe.id}`,
                        label: recipe.name,
                        icon: "hat-chef",
                        route: "RecipesView",
                        routeQuery: { search: recipe.name },
                        group: "records",
                        kind: "Recipe",
                        sectionTitle: "Recipes",
                        keywords: recipe.tags || [],
                        description: recipe.description || "Open in Recipes"
                    });
                }
            }
            if (userIsRole("admin") && hasFeatureFlag(Flags.SuppliersModule)) {
                for (const supplier of this.$store.state.supplierList || []) {
                    items.push({
                        id: `supplier.${supplier.id}`,
                        label: supplier.name,
                        icon: "industry-alt",
                        path: `/settings/suppliers?search=${encodeURIComponent(supplier.name)}`,
                        group: "records",
                        kind: "Supplier",
                        sectionTitle: "Settings · Suppliers",
                        keywords: [supplier.goods_supplied, supplier.reference].filter(Boolean),
                        description: supplier.goods_supplied || "Open in Suppliers"
                    });
                }
            }
            if (userIsRole("admin")) {
                for (const user of this.$store.state.userList || []) {
                    const name =
                        [user.first_name, user.last_name].filter(Boolean).join(" ") ||
                        user.email_address;
                    if (!name) continue;
                    items.push({
                        id: `user.${user.id}`,
                        label: name,
                        icon: "user",
                        path: `/settings/users?search=${encodeURIComponent(name)}`,
                        group: "records",
                        kind: "User",
                        sectionTitle: "Settings · Users",
                        keywords: [user.email_address, user.phone_number, user.role].filter(
                            Boolean
                        ),
                        description: user.email_address || user.role || "Open in Users"
                    });
                }
            }
            return items;
        },
        visibleRemoteRecordItems() {
            if (!this.visibilityContext.planScoped) return this.remoteRecords;
            const settings = new Set(visibleSettingsSections(this.visibilityContext).map((item) => item.id));
            return this.remoteRecords.filter((item) => {
                const target = item.path || { name: item.route, params: item.routeParams, query: item.routeQuery };
                const route = this.$router.resolve(target);
                if (route.meta.featureFlag && !hasFeatureFlag(route.meta.featureFlag)) return false;
                return route.name !== "Settings" || settings.has(route.params.section);
            });
        },
        allSearchableItems() {
            const items = [
                ...this.actionItems,
                ...this.flatItems,
                ...this.localRecordItems,
                ...this.visibleRemoteRecordItems,
                ...this.helpItems
            ];
            const unique = new Map();
            for (const item of items) {
                const existing = unique.get(item.id);
                if (!existing) {
                    unique.set(item.id, item);
                } else if (item.serverMatch) {
                    unique.set(item.id, { ...item, ...existing, serverMatch: true });
                }
            }
            return [...unique.values()];
        },
        resultGroups() {
            if (this.query.trim())
                return groupedCommandResults(this.allSearchableItems, this.query);
            const currentItems = new Map(
                [...this.actionItems, ...this.flatItems].map((item) => [item.id, item])
            );
            const recent = this.recentIDs.map((id) => currentItems.get(id)).filter(Boolean);
            const suggestions = [
                ...this.actionItems,
                ...this.flatItems.filter((item) => item.group === "pages")
            ]
                .filter((item) => !recent.some((recentItem) => recentItem.id === item.id))
                .slice(0, RECENT_LIMIT);
            const groups = [];
            if (recent.length)
                groups.push({ id: "recent", label: GROUP_LABELS.recent, items: recent });
            if (suggestions.length)
                groups.push({ id: "actions", label: "Suggested", items: suggestions });
            return groups;
        },
        results() {
            return flattenCommandGroups(this.resultGroups);
        }
    },
    watch: {
        query() {
            this.selectedIndex = this.isMobile && !this.query ? -1 : 0;
            this.scheduleRemoteRecordLoad();
            this.$nextTick(() => this.scrollSelectedIntoView());
        },
        open(value) {
            this.$store.commit("setCommandPaletteOpen", value);
            if (value) {
                this.returnFocusTo ??= dialogTrigger();
                this.query = "";
                this.loadRecents();
                this.selectedIndex = this.isMobile ? -1 : 0;
                this.setDocumentScrollLocked(true);
                this.registerNativeBackHandler();
                this.$nextTick(() => {
                    if (this.open) this.$refs.input?.focus({ preventScroll: true });
                });
            } else {
                this.setDocumentScrollLocked(false);
                this.removeNativeBackHandler();
                const trigger = this.returnFocusTo;
                this.returnFocusTo = null;
                this.$nextTick(() => {
                    if (!this.open && trigger?.isConnected && trigger.getClientRects().length) {
                        trigger.focus({ preventScroll: true });
                    }
                });
            }
        },
        "$route.fullPath"() {
            this.returnFocusTo = null;
            if (this.open) this.close();
        }
    },
    mounted() {
        this.acceptAsyncResults = true;
        this.loadRecents();
        window.addEventListener("keydown", this.onGlobalKey);
        emitter.on("command-palette:open", this.openPalette);
        this.loadHelpItems();
    },
    unmounted() {
        this.acceptAsyncResults = false;
        clearTimeout(this.remoteSearchTimer);
        this.remoteSearchController?.abort();
        this.$store.commit("setCommandPaletteOpen", false);
        this.setDocumentScrollLocked(false);
        this.removeNativeBackHandler();
        window.removeEventListener("keydown", this.onGlobalKey);
        emitter.off("command-palette:open", this.openPalette);
    },
    methods: {
        recentStorageKey() {
            const tenant = this.$store.getters.domain || this.$store.state.customerID || "default";
            return `command-palette.recents.${tenant}`;
        },
        loadRecents() {
            try {
                const value = JSON.parse(localStorage.getItem(this.recentStorageKey()) || "[]");
                this.recentIDs = Array.isArray(value) ? value.slice(0, RECENT_LIMIT) : [];
            } catch {
                this.recentIDs = [];
            }
        },
        rememberCommand(item) {
            if (
                !item ||
                item.group === "records" ||
                item.group === "help" ||
                item.action === "logout"
            )
                return;
            this.recentIDs = [item.id, ...this.recentIDs.filter((id) => id !== item.id)].slice(
                0,
                RECENT_LIMIT
            );
            try {
                localStorage.setItem(this.recentStorageKey(), JSON.stringify(this.recentIDs));
            } catch {
                // Navigation must continue if storage is unavailable.
            }
        },
        setDocumentScrollLocked(locked) {
            if (typeof document === "undefined") return;
            document.documentElement.classList.toggle("command-palette-open", locked);
            document.body.classList.toggle("command-palette-open", locked);
        },
        async registerNativeBackHandler() {
            if (!this.isMobileOS || this.nativeBackHandler) return;
            const registration = ++this.nativeBackRegistration;
            try {
                const { App } = await import("@capacitor/app");
                const handler = await App.addListener("backButton", () => {
                    if (this.open) this.close();
                });
                if (registration !== this.nativeBackRegistration || !this.open) {
                    await handler.remove();
                    return;
                }
                this.nativeBackHandler = handler;
            } catch {
                // Browser builds and unsupported native shells use the visible close control.
            }
        },
        removeNativeBackHandler() {
            this.nativeBackRegistration += 1;
            const handler = this.nativeBackHandler;
            this.nativeBackHandler = null;
            if (handler?.remove) Promise.resolve(handler.remove()).catch(() => {});
        },
        async loadHelpItems() {
            try {
                const articles = await loadHelpSearchIndex();
                if (!this.acceptAsyncResults) return;
                this.helpItems = articles.map((article) => ({
                    id: `help.${article.id}`,
                    label: article.title,
                    icon: "question-circle",
                    href: article.url,
                    group: "help",
                    kind: "Help",
                    sectionTitle: article.category,
                    description: article.description,
                    searchText: article.searchText,
                    source: "help"
                }));
            } catch {
                // Local navigation remains available while offline.
            }
        },
        scheduleRemoteRecordLoad() {
            clearTimeout(this.remoteSearchTimer);
            this.remoteSearchController?.abort();
            const query = this.query.trim();
            if (query.length < REMOTE_QUERY_LENGTH) {
                this.remoteRecords = [];
                return;
            }
            this.remoteSearchTimer = setTimeout(() => this.loadRemoteRecords(query), 250);
        },
        remoteCacheKey(query) {
            return [
                this.$store.getters.domain || this.$store.state.customerID || "default",
                this.$store.getters.userRole?.() || "unknown",
                this.$store.state.locationID || "",
                this.$store.state.departmentID || "",
                query.toLowerCase()
            ].join("|");
        },
        async loadRemoteRecords(query) {
            if (query !== this.query.trim() || query.length < REMOTE_QUERY_LENGTH) return;
            if (userIsRole("supplier")) return;
            const locationID = this.$store.state.locationID;
            const departmentID = this.$store.state.departmentID;
            const cacheKey = this.remoteCacheKey(query);
            const cached = this.remoteSearchCache.get(cacheKey);
            if (cached && Date.now() - cached.time < REMOTE_CACHE_TTL) {
                this.remoteRecords = cached.items;
                return;
            }

            const controller = new AbortController();
            this.remoteSearchController = controller;
            try {
                const response = await API.Search.List(
                    {
                        q: query,
                        limit: 5,
                        ...(locationID ? { location_id: locationID } : {}),
                        ...(departmentID ? { department_id: departmentID } : {})
                    },
                    { signal: controller.signal }
                );
                if (
                    !this.acceptAsyncResults ||
                    controller.signal.aborted ||
                    query !== this.query.trim()
                )
                    return;
                const items = (response?.groups || []).flatMap((group) =>
                    this.mapRemoteRecords(group.type, group.items || []).map((item) => ({
                        ...item,
                        serverMatch: true
                    }))
                );
                this.remoteSearchCache.set(cacheKey, { time: Date.now(), items });
                this.remoteRecords = items;
            } catch (error) {
                if (!controller.signal.aborted && this.acceptAsyncResults) this.remoteRecords = [];
            } finally {
                if (this.remoteSearchController === controller) this.remoteSearchController = null;
            }
        },
        mapRemoteRecords(type, records) {
            if (type === "product") {
                return records.map((product) => ({
                    id: `product.${product.id}`,
                    label: product.title || "Product",
                    icon: "burger-soda",
                    route: "ProductView",
                    routeParams: { id: product.id },
                    group: "records",
                    kind: "Product",
                    sectionTitle: "Products",
                    description: product.subtitle || "Open product"
                }));
            }
            if (type === "file") {
                return records.map((file) => ({
                    id: `file.${file.id}`,
                    label: file.title,
                    icon: "file",
                    route: "FilesView",
                    routeQuery: { search: file.title },
                    group: "records",
                    kind: "File",
                    sectionTitle: "Files",
                    description: file.subtitle || "Open in Files"
                }));
            }
            if (type === "check") {
                return records.map((check) => ({
                    id: `check.${check.id}`,
                    label: check.title || "Completed check",
                    icon: "clipboard-check",
                    route: "CheckRecord",
                    routeParams: { id: check.id },
                    group: "records",
                    kind: "Check",
                    sectionTitle: "Checks",
                    keywords: ["completed check"],
                    description: check.subtitle || "Completed check"
                }));
            }
            if (type === "recipe") {
                return records.map((recipe) => ({
                    id: `recipe.${recipe.id}`,
                    label: recipe.title,
                    icon: "hat-chef",
                    route: "RecipesView",
                    routeQuery: { search: recipe.title },
                    group: "records",
                    kind: "Recipe",
                    sectionTitle: "Recipes",
                    description: recipe.subtitle || "Open in Recipes"
                }));
            }
            if (type === "supplier") {
                return records.map((supplier) => ({
                    id: `supplier.${supplier.id}`,
                    label: supplier.title,
                    icon: "industry-alt",
                    path: `/settings/suppliers?search=${encodeURIComponent(supplier.title)}`,
                    group: "records",
                    kind: "Supplier",
                    sectionTitle: "Settings · Suppliers",
                    description: supplier.subtitle || "Open in Suppliers"
                }));
            }
            if (type === "user") {
                return records.map((user) => ({
                    id: `user.${user.id}`,
                    label: user.title,
                    icon: "user",
                    path: `/settings/users?search=${encodeURIComponent(user.title)}`,
                    group: "records",
                    kind: "User",
                    sectionTitle: "Settings · Users",
                    description: user.subtitle || "Open in Users"
                }));
            }
            if (type === "check_form") {
                return records.map((form) => ({
                    id: `check-form.${form.id}`,
                    label: userIsRole("auditor") ? form.title : `Start ${form.title}`,
                    icon: "clipboard-check",
                    route: userIsRole("auditor") ? "CheckFormsView" : "ChecksView",
                    routeQuery: userIsRole("auditor") ? { search: form.title } : { form: form.id },
                    group: "records",
                    kind: "Check form",
                    sectionTitle: "Checks",
                    description: form.subtitle || "Open check form"
                }));
            }
            return [];
        },
        onGlobalKey(event) {
            const isToggle = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k";
            if (isToggle) {
                event.preventDefault();
                if (this.open) this.close();
                else this.openPalette();
            }
        },
        openPalette({ trigger } = {}) {
            if (this.open) return;
            const drawer = mobileNavigation();
            this.returnFocusTo = drawer?.matches(".show, .showing, .hiding")
                ? mobileNavigationLauncher()
                : dialogTrigger(trigger || document.activeElement);
            hideMobileNavigation();
            this.open = true;
        },
        clearQuery() {
            this.query = "";
            this.$nextTick(() => {
                if (this.open) this.$refs.input?.focus({ preventScroll: true });
            });
        },
        onPanelKey(event) {
            if (event.key === "Escape") {
                event.preventDefault();
                this.close();
            } else if (event.key === "Tab" && !event.altKey && !event.ctrlKey && !event.metaKey) {
                const controls = SelectorEngine.focusableChildren(this.$refs.panel);
                if (!controls.length) return;
                // Safari can skip buttons in its default Tab order. Own each
                // step so Shift+Tab from the input cannot escape to browser UI.
                const current = controls.indexOf(document.activeElement);
                const next = current < 0
                    ? event.shiftKey ? controls.length - 1 : 0
                    : (current + (event.shiftKey ? -1 : 1) + controls.length) % controls.length;
                event.preventDefault();
                controls[next].focus({ preventScroll: true });
            }
        },
        onKey(event) {
            if (event.key === "ArrowDown") {
                event.preventDefault();
                this.selectedIndex = Math.min(this.selectedIndex + 1, this.results.length - 1);
                this.scrollSelectedIntoView();
                this.preloadSelectedResult();
            } else if (event.key === "ArrowUp") {
                event.preventDefault();
                this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
                this.scrollSelectedIntoView();
                this.preloadSelectedResult();
            } else if (event.key === "Enter") {
                event.preventDefault();
                const item = this.results[this.selectedIndex];
                if (item) this.select(item);
            }
        },
        resultIndex(item) {
            return this.results.findIndex((result) => result.id === item.id);
        },
        preview(item) {
            this.selectedIndex = this.resultIndex(item);
            this.preloadItem(item);
        },
        preloadSelectedResult() {
            this.preloadItem(this.results[this.selectedIndex]);
        },
        preloadItem(item) {
            if (!item) return;
            if (item.route) {
                preloadRoute(this.$router, {
                    name: item.route,
                    ...(item.routeParams ? { params: item.routeParams } : {}),
                    ...(item.routeQuery ? { query: item.routeQuery } : {})
                });
            } else if (item.path) {
                preloadRoute(this.$router, item.path);
            }
        },
        itemMeta(item) {
            return [...new Set([item.sectionTitle, item.matchDetail].filter(Boolean))].join(" · ");
        },
        scrollSelectedIntoView() {
            if (!this.$refs.list || this.selectedIndex < 0) return;
            this.$refs.list
                .querySelector(`[data-result-index="${this.selectedIndex}"]`)
                ?.scrollIntoView?.({ block: "nearest" });
        },
        select(item) {
            this.rememberCommand(item);
            // Navigation or another dialog owns focus after a command runs.
            this.returnFocusTo = null;
            this.close();
            if (item.route) {
                this.$router.push({
                    name: item.route,
                    ...(item.routeParams ? { params: item.routeParams } : {}),
                    ...(item.routeQuery ? { query: item.routeQuery } : {})
                });
            } else if (item.path) {
                this.$router.push(item.path);
            } else if (item.href) {
                window.open(item.href, "_blank", "noopener");
            } else if (item.action === "logout") {
                this.doLogout();
            } else if (item.action === "help-page") {
                window.open(resolveHelpForRoute(this.$route).url, "_blank", "noopener,noreferrer");
            } else if (item.action === "scan-label") {
                emitter.emit(SCAN_LABEL_OPEN);
            } else if (item.action === "support") {
                if (typeof window.Tawk_API !== "undefined" && window.Tawk_API.toggle)
                    window.Tawk_API.toggle();
            } else if (item.action === "switch-location") {
                this.$store.commit("setLocationByID", item.targetID);
                this.$store.state.$toast?.success(
                    `Changed location to ${this.$store.state.location?.name}`
                );
            } else if (item.action === "switch-department") {
                this.$store.commit("setDepartmentByID", item.targetID);
                this.$store.state.$toast?.success(
                    `Changed department to ${this.$store.state.department?.name}`
                );
            }
        },
        async doLogout() {
            await API.logoutUser();
        },
        close() {
            clearTimeout(this.remoteSearchTimer);
            this.remoteSearchController?.abort();
            this.open = false;
        }
    }
};
</script>

<style scoped>
.cp-overlay {
    position: fixed;
    inset: 0;
    z-index: 21000;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding-top: 12vh;
    background: rgba(0, 0, 0, 0.52);
    backdrop-filter: blur(3px);
    overscroll-behavior: none;
}

.cp-panel {
    display: flex;
    width: min(680px, 92vw);
    max-height: 72vh;
    flex-direction: column;
    overflow: hidden;
    color: white;
    background: #101010;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 0.8rem;
    box-shadow: 0 24px 70px rgba(0, 0, 0, 0.65);
}

.cp-input-row {
    display: flex;
    align-items: center;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.07);
}

.cp-search-icon {
    margin-right: 0.75rem;
    color: rgba(255, 255, 255, 0.5);
}

.cp-input {
    min-width: 0;
    flex: 1;
    padding: 0.25rem 0;
    color: white;
    font-size: 1rem;
    background: transparent;
    border: 0;
    outline: 0;
}

.cp-input::-webkit-search-cancel-button {
    display: none;
}
.cp-input::placeholder {
    color: rgba(255, 255, 255, 0.4);
}

.cp-icon-button {
    display: inline-flex;
    width: 2.75rem;
    height: 2.75rem;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    padding: 0;
    color: rgba(255, 255, 255, 0.65);
    background: transparent;
    border: 0;
    border-radius: 50%;
}

.cp-icon-button:hover,
.cp-icon-button:focus-visible {
    color: white;
    background: rgba(255, 255, 255, 0.08);
    outline: 0;
}

.cp-list {
    flex: 1;
    overflow-y: auto;
    padding: 0.35rem;
    overscroll-behavior: contain;
    scrollbar-color: rgba(255, 255, 255, 0.22) transparent;
    scrollbar-width: thin;
}

.cp-group + .cp-group {
    margin-top: 0.25rem;
}

.cp-group-title {
    padding: 0.55rem 0.75rem 0.3rem;
    color: rgba(255, 255, 255, 0.4);
    font-size: 0.68rem;
    font-weight: 600;
    letter-spacing: 0.09em;
    text-transform: uppercase;
}

.cp-item {
    display: flex;
    width: 100%;
    min-height: 3.25rem;
    align-items: center;
    gap: 0.75rem;
    padding: 0.5rem 0.75rem;
    color: white;
    font: inherit;
    text-align: left;
    cursor: pointer;
    user-select: none;
    background: transparent;
    border: 0;
    border-radius: 0.5rem;
}

.cp-item-active {
    background: rgba(255, 255, 255, 0.09);
}

.cp-item-icon {
    width: 1.25rem;
    flex-shrink: 0;
    color: rgba(255, 255, 255, 0.7);
    text-align: center;
}

.cp-item-text {
    min-width: 0;
    flex: 1;
}

.cp-item-label {
    overflow: hidden;
    color: white;
    font-size: 0.94rem;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.cp-item-section {
    overflow: hidden;
    margin-top: 0.1rem;
    color: rgba(255, 255, 255, 0.47);
    font-size: 0.74rem;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.cp-kind {
    flex-shrink: 0;
    padding: 0.18rem 0.42rem;
    color: rgba(255, 255, 255, 0.5);
    font-size: 0.65rem;
    background: rgba(255, 255, 255, 0.055);
    border: 1px solid rgba(255, 255, 255, 0.075);
    border-radius: 999px;
}

.cp-item-arrow {
    flex-shrink: 0;
    color: rgba(255, 255, 255, 0.28);
    font-size: 0.72rem;
    opacity: 0;
}

.cp-item-active .cp-item-arrow {
    opacity: 1;
}

.cp-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.65rem;
    padding: 2.5rem 1rem;
    color: rgba(255, 255, 255, 0.4);
    text-align: center;
}

.cp-hints {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    padding: 0.5rem 1rem;
    color: rgba(255, 255, 255, 0.5);
    font-size: 0.75rem;
    background: rgba(0, 0, 0, 0.28);
    border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.cp-hints kbd {
    margin-right: 0.15rem;
    padding: 0.05rem 0.35rem;
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.7rem;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 0.25rem;
}

:global(html.command-palette-open),
:global(body.command-palette-open) {
    overflow: hidden;
}

@media (max-width: 767.98px) {
    .cp-overlay {
        padding: 0;
        background: #101010;
        backdrop-filter: none;
    }
    .cp-panel {
        width: 100vw;
        height: 100vh;
        height: 100dvh;
        max-height: none;
        padding: var(--forkto-safe-area-top) var(--forkto-safe-area-right)
            var(--forkto-safe-area-bottom) var(--forkto-safe-area-left);
        border: 0;
        border-radius: 0;
        box-shadow: none;
    }
    .cp-input-row {
        min-height: 3.75rem;
        padding: 0.5rem;
    }
    .cp-list {
        padding: 0.35rem 0.5rem 0.75rem;
    }
    .cp-item {
        min-height: 3.65rem;
        padding: 0.6rem 0.75rem;
    }
    .cp-kind {
        display: none;
    }
    .cp-item-arrow {
        opacity: 0.55;
    }
}
</style>
